class ThumbnailCacheService {
  constructor() {
    this.TTL = 9 * 1000; // 10 seconds in milliseconds (slightly less than the 60-minute ttl)
    this.STORAGE_KEY = 'thumbnail_cache';
    this._initializeCache();
  }

  /**
   * Initialize the cache from sessionStorage or create an empty one
   * @private
   */
  _initializeCache() {
    try {
      const storedCache = sessionStorage.getItem(this.STORAGE_KEY);
      this.cache = new Map(storedCache ? JSON.parse(storedCache) : []);
    } catch (error) {
      console.error('Failed to initialize thumbnail cache from sessionStorage:', error);
      this.cache = new Map();
    }
  }

  /**
   * Save the cache to sessionStorage
   * @private
   */
  _saveCache() {
    try {
      sessionStorage.setItem(this.STORAGE_KEY, JSON.stringify([...this.cache]));
    } catch (error) {
      console.error('Failed to save thumbnail cache to sessionStorage:', error);
    }
  }

  /**
   * Get a cached thumbnail URL
   * @param {string} documentId - The document ID
   * @param {string} page - The page number
   * @param {string} size - The thumbnail size ('small' or 'large'), defaults to 'small'
   * @returns {string|null} - The cached URL or null if not found/expired
   */
  get(documentId, page, size = 'small') {
    const key = this._getCacheKey(documentId, page, size);
    const cached = this.cache.get(key);

    if (!cached) return null;

    if (Date.now() > cached.ttl) {
      this.cache.delete(key);
      this._saveCache();
      return null;
    }

    return cached.url;
  }

  /**
   * Set a thumbnail URL in the cache
   * @param {string} documentId - The document ID
   * @param {string} page - The page number
   * @param {string} url - The pre-signed URL
   * @param {string} size - The thumbnail size ('small' or 'large'), defaults to 'small'
   */
  set(documentId, page, url, size = 'small') {
    const key = this._getCacheKey(documentId, page, size);
    this.cache.set(key, {
      url,
      ttl: Date.now() + this.TTL,
    });
    this._saveCache();
  }

  /**
   * Clear expired entries from the cache
   */
  clearExpired() {
    const now = Date.now();
    let hasChanges = false;

    for (const [key, value] of this.cache.entries()) {
      if (now > value.ttl) {
        this.cache.delete(key);
        hasChanges = true;
      }
    }

    if (hasChanges) {
      this._saveCache();
    }
  }

  /**
   * Clear the entire cache
   */
  clear() {
    this.cache.clear();
    this._saveCache();
  }

  /**
   * Get all cached variants for a document and page type
   * @param {string} documentId - The document ID
   * @param {string} page - The page/type identifier (e.g., 'imported')
   * @returns {Array} - Array of cached image objects or empty array if none found
   */
  getAllVariants(documentId, page) {
    const results = [];
    const prefix = `${documentId}-${page}-`;
    const now = Date.now();

    for (const [key, value] of this.cache.entries()) {
      if (key.startsWith(prefix)) {
        if (now > value.ttl) {
          this.cache.delete(key);
          continue;
        }

        // Extract variant from cache key (e.g., "docId-imported-original" -> "original")
        const variantKey = key.slice(prefix.length);

        // Use the cached URL to determine the real file name/variant.
        // Strip query/hash, take last path segment (could be 'original.png' or 'cropped-...png' or similar),
        // and derive the variant from that. This handles URLs like
        // .../original.png/cropped-abc.png?... where the last segment is the true variant.
        const urlNoParams = (value.url || '').split('?')[0].split('#')[0];
        const lastSegment = urlNoParams.split('/').pop() || '';
        const isCropped = lastSegment.toLowerCase().startsWith('cropped-');

        let name;
        let variant;

        if (isCropped) {
          name = lastSegment;
          variant = 'cropped';
        } else if (lastSegment && /\.[a-zA-Z0-9]+$/.test(lastSegment)) {
          // last segment includes an extension (e.g., original.png)
          name = lastSegment;
          // variant fallback: if variantKey looks like a filename without ext, use it; otherwise derive from lastSegment
          variant = variantKey || lastSegment.replace(/\.[^.]+$/, '');
        } else {
          // fallback to using the variantKey
          name = `${variantKey}.png`;
          variant = variantKey;
        }

        results.push({
          id: documentId,
          url: value.url,
          name,
          variant,
        });
      }
    }

    return results;
  }

  /**
   * Generate a cache key for a document, page, and size
   * @private
   */
  _getCacheKey(documentId, page, size = 'small') {
    return `${documentId}-${page}-${size}`;
  }
}

// Export a singleton instance
export const thumbnailCache = new ThumbnailCacheService();

// Clear expired entries every minute
setInterval(() => {
  thumbnailCache.clearExpired();
}, 60 * 1000);
